import { defineMessages } from 'react-intl';

const scope =
  'app.containers.WorkflowEditor.components.StageDetail.DataCollectionStage.EditQuestionsModal';

export const messages = defineMessages({
  title: {
    id: `${scope}.title`,
    defaultMessage: 'Edit questions',
    description:
      "Heading of the modal that edits a data collection stage's questions",
  },
  formTitle: {
    id: `${scope}.formTitle`,
    defaultMessage: 'Form title',
    description: 'Label for the form title shown to applicants',
  },
  formDescription: {
    id: `${scope}.formDescription`,
    defaultMessage: 'Form description',
    description: 'Label for the form description shown to applicants',
  },
  formDescriptionPlaceholder: {
    id: `${scope}.formDescriptionPlaceholder`,
    defaultMessage: 'Tell applicants what this form is for',
    description: 'Placeholder in the form description editor',
  },
  questions: {
    id: `${scope}.questions`,
    defaultMessage: 'Questions',
    description: 'Section heading above the list of questions',
  },
  questionPlaceholder: {
    id: `${scope}.questionPlaceholder`,
    defaultMessage: 'Type the question applicants will see',
    description: 'Placeholder in the question wording input',
  },
  hint: {
    id: `${scope}.hint`,
    defaultMessage: 'Hint',
    description: 'Label for the optional hint under a question',
  },
  addHint: {
    id: `${scope}.addHint`,
    defaultMessage: '+ Add hint (optional)',
    description: 'Link that reveals the hint input',
  },
  hintPlaceholder: {
    id: `${scope}.hintPlaceholder`,
    defaultMessage: 'Add a hint to help applicants answer',
    description: 'Placeholder in the hint input',
  },
  dataKey: {
    id: `${scope}.dataKey`,
    defaultMessage: 'Data key',
    description: 'Label next to the key a question stores its answer under',
  },
  enterDataKey: {
    id: `${scope}.enterDataKey`,
    defaultMessage: 'Enter data key',
    description: 'Button text when a question has no key yet',
  },
  customKeyPlaceholder: {
    id: `${scope}.customKeyPlaceholder`,
    defaultMessage: 'custom_key',
    description: 'Placeholder for a typed custom key, snake_case',
  },
  standardKeysHeading: {
    id: `${scope}.standardKeysHeading`,
    defaultMessage: 'Standard keys, no mapping required',
    description: 'Heading of the standard key picker',
  },
  standardKeysDescription: {
    id: `${scope}.standardKeysDescription`,
    defaultMessage:
      'They sync straight to the worker profile and to your HR system, with no setup work.',
    description: 'Helper text under the standard key picker heading',
  },
  searchStandardKeys: {
    id: `${scope}.searchStandardKeys`,
    defaultMessage: 'Search standard keys',
    description: 'Placeholder of the search input in the standard key picker',
  },
  noStandardKeyResults: {
    id: `${scope}.noStandardKeyResults`,
    defaultMessage: 'No standard keys match "{query}"',
    description:
      'Empty state when the picker search matches nothing; {query} is the typed text',
  },
  alreadyOnThisForm: {
    id: `${scope}.alreadyOnThisForm`,
    defaultMessage: 'already on this form',
    description:
      'Replaces the key of a standard question already used on this form',
  },
  createCustomKey: {
    id: `${scope}.createCustomKey`,
    defaultMessage: 'Other, create a custom key',
    description:
      'Footer action in the picker that switches to a typed custom key',
  },
  createCustomKeyDescription: {
    id: `${scope}.createCustomKeyDescription`,
    defaultMessage:
      'Syncs too, but reaching your HR system needs custom mapping work',
    description: 'Helper text under the create custom key action',
  },
  standardKeyBadge: {
    id: `${scope}.standardKeyBadge`,
    defaultMessage: 'Standard',
    description:
      'Badge on a key bound to a standard question, and the link that opens the picker',
  },
  standardKeyChip: {
    id: `${scope}.standardKeyChip`,
    defaultMessage: 'Standard key · no mapping required',
    description: 'Chip next to a key bound to a standard question',
  },
  standardKeyApplied: {
    id: `${scope}.standardKeyApplied`,
    defaultMessage: 'Now uses the {title} standard key',
    description:
      'Undo bar text after a question is bound to a standard key; {title} is the standard question name',
  },
  undoStandardKey: {
    id: `${scope}.undoStandardKey`,
    defaultMessage: 'Undo key change',
    description:
      'Button in the undo bar that restores the key, answer type and options the question had before',
  },
  answerType: {
    id: `${scope}.answerType`,
    defaultMessage: 'Answer type',
    description: 'Accessible label of the answer type select',
  },
  answerTypeLocked: {
    id: `${scope}.answerTypeLocked`,
    defaultMessage: 'A standard key sets the answer type',
    description:
      'Tooltip on the answer type select when a standard key fixes it',
  },
  typeTextField: {
    id: `${scope}.typeTextField`,
    defaultMessage: 'Short answer',
    description: 'Answer type: one line of text',
  },
  typeTextArea: {
    id: `${scope}.typeTextArea`,
    defaultMessage: 'Paragraph',
    description: 'Answer type: several lines of text',
  },
  typeRadio: {
    id: `${scope}.typeRadio`,
    defaultMessage: 'Multiple choice',
    description: 'Answer type: pick one option',
  },
  typeCheckboxes: {
    id: `${scope}.typeCheckboxes`,
    defaultMessage: 'Checkboxes',
    description: 'Answer type: pick several options',
  },
  typeDropdown: {
    id: `${scope}.typeDropdown`,
    defaultMessage: 'Dropdown',
    description: 'Answer type: pick one option from a dropdown',
  },
  typeDatepicker: {
    id: `${scope}.typeDatepicker`,
    defaultMessage: 'Date',
    description: 'Answer type: a date',
  },
  typeFile: {
    id: `${scope}.typeFile`,
    defaultMessage: 'File upload',
    description: 'Answer type: an uploaded file',
  },
  typeAddress: {
    id: `${scope}.typeAddress`,
    defaultMessage: 'Address',
    description: 'Answer type: a postal address',
  },
  typeOther: {
    id: `${scope}.typeOther`,
    defaultMessage: 'Other',
    description: 'Answer type not offered in the select',
  },
  restricted: {
    id: `${scope}.restricted`,
    defaultMessage: 'Restricted',
    description:
      'Checkbox and badge for an answer only secure data users can read',
  },
  restrictedDescription: {
    id: `${scope}.restrictedDescription`,
    defaultMessage: 'Only users with secure data access can read the answer',
    description: 'Tooltip on the restricted checkbox',
  },
  secureDescription: {
    id: `${scope}.secureDescription`,
    defaultMessage:
      'Stored encrypted. Only users with secure data access can read the answer',
    description:
      'Tooltip on the restricted badge of a secure standard question',
  },
  options: {
    id: `${scope}.options`,
    defaultMessage: 'Options',
    description: 'Label above the answer options of a choice question',
  },
  optionPlaceholder: {
    id: `${scope}.optionPlaceholder`,
    defaultMessage: 'Option {number}',
    description: 'Placeholder of an option input; {number} is its position',
  },
  addOption: {
    id: `${scope}.addOption`,
    defaultMessage: 'Add option',
    description: 'Button that adds an answer option',
  },
  removeOption: {
    id: `${scope}.removeOption`,
    defaultMessage: 'Remove option',
    description: 'Accessible label of the button that removes an option',
  },
  standardOptionsNote: {
    id: `${scope}.standardOptionsNote`,
    defaultMessage: 'Options come with the standard key and cannot change',
    description:
      'Helper text under the read-only options of a standard question',
  },
  moreOptions: {
    id: `${scope}.moreOptions`,
    defaultMessage: '+{count} more',
    description: 'Chip counting options not shown; {count} is the hidden count',
  },
  addQuestion: {
    id: `${scope}.addQuestion`,
    defaultMessage: 'Add question',
    description: 'Button that adds a question to the form',
  },
  removeQuestion: {
    id: `${scope}.removeQuestion`,
    defaultMessage: 'Remove question',
    description: 'Accessible label of the button that removes a question',
  },
  dragQuestion: {
    id: `${scope}.dragQuestion`,
    defaultMessage: 'Drag to reorder',
    description: 'Accessible label of the drag handle that reorders questions',
  },
  saveQuestions: {
    id: `${scope}.saveQuestions`,
    defaultMessage: 'Save questions',
    description: 'Primary button that saves the form',
  },
  noQuestionsTitle: {
    id: `${scope}.noQuestionsTitle`,
    defaultMessage: 'No questions yet',
    description: 'Empty state title when the form has no questions',
  },
  noQuestionsDescription: {
    id: `${scope}.noQuestionsDescription`,
    defaultMessage: 'Add a question to start collecting data on this stage.',
    description: 'Empty state description when the form has no questions',
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage:
      "We couldn't load the questions. Close this window and try again.",
    description: 'Error when the questions could not be loaded',
  },
  saveError: {
    id: `${scope}.saveError`,
    defaultMessage:
      "We couldn't save the questions. Fix the errors below and try again.",
    description: 'Error when saving the questions failed',
  },
  blockerCount: {
    id: `${scope}.blockerCount`,
    defaultMessage:
      '{count, plural, one {# field needs} other {# fields need}} your attention',
    description:
      'Alert counting fields with validation errors; {count} is the number',
  },
  errorQuestionRequired: {
    id: `${scope}.errorQuestionRequired`,
    defaultMessage: 'Enter the question text',
    description: 'Validation error when the question wording is empty',
  },
  errorKeyRequired: {
    id: `${scope}.errorKeyRequired`,
    defaultMessage: 'Choose a standard key or enter a custom key',
    description: 'Validation error when a question has no key',
  },
  errorKeyFormat: {
    id: `${scope}.errorKeyFormat`,
    defaultMessage: 'Use only lowercase letters, numbers, and underscores',
    description: 'Validation error when a custom key has invalid characters',
  },
  errorKeyDuplicate: {
    id: `${scope}.errorKeyDuplicate`,
    defaultMessage: 'Another question on this form uses this key',
    description: 'Validation error when two questions share a key',
  },
  errorOptionsRequired: {
    id: `${scope}.errorOptionsRequired`,
    defaultMessage: 'Add at least one option',
    description: 'Validation error when a choice question has no options',
  },
});
